/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// AttendanceGate  (Phase 2 — updated Phase 3.5)
//
// "Did the patient attend?" question + Yes/No buttons.
//
// Two rendering variants:
//   variant="card"         — standalone centred card (360 ms appear + dismiss
//                            animations). Used if rendered outside a host card.
//   variant="inline-hero"  — question + buttons only; NO card chrome, NO
//                            appear animation. Parent (AttendedAppointment hero
//                            card) handles the Collapse animation.
//
// Yes → dispatch RecordAttended → on success call onDismissed.
// No  → call handleNoAttended (parent closes popup + opens FTA).
//
// Button style: CRM standard (green #00AB55, borderRadius 6px) matching the
// Finish / Cancel buttons in AttendedAppointment.jsx.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Stack, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { dispatch, useSelector } from 'src/redux/store';
import { RecordAttended } from 'src/redux/slices/lead';
import capitalizeFirstLetter from 'src/utils/capitalizedFunc';
import { getTreatmentDate } from 'src/utils/multiTreatmentFlow';
import Iconify from 'src/components/Iconify';
import { CRM_PRIMARY_BUTTON_SX, CRM_SECONDARY_BUTTON_SX } from 'src/styles/crmButtons';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const APPEAR_MS = 240;
const DISMISS_MS = 220;

// ---------------------------------------------------------------------------

export default function AttendanceGate({ leadDetail, handleNoAttended, onDismissed, variant, appointmentSelection }) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const { isLoader } = useSelector((state) => state.loaderReducer);

    // Card variant only — appear + dismiss animations.
    const [appeared, setAppeared] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const successCalledRef = useRef(false);
    // Focus the Yes button on mount so keyboard users don't have to Tab to it.
    const yesBtnRef = useRef(null);

    useEffect(() => {
        if (variant !== 'card') return undefined;
        const t = setTimeout(() => setAppeared(true), 16);
        return () => clearTimeout(t);
    }, [variant]);

    // Focus the Yes button after the appear animation so screen-reader users
    // land on the primary action without extra Tab presses.
    useEffect(() => {
        const t = setTimeout(() => yesBtnRef.current?.focus(), 32);
        return () => clearTimeout(t);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------------------------------------------------------------------------
    // Derive appointment meta from leadDetail (card variant subtitle only)
    // ---------------------------------------------------------------------------
    const bookedAppt = appointmentSelection?.appointment || (leadDetail?.lead_appointment || []).find(
        (a) => a.status === 'BOOKED' || a.status === 'CONSULTATION_BOOKED'
    );
    const apptDateLabel = bookedAppt?.datetime ? getTreatmentDate(bookedAppt.datetime) : null;
    const practitioners = bookedAppt?.assignees || [];
    const practitionerLabel = practitioners.length > 0
        ? practitioners
            .map((a) =>
                `${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`.trim()
            )
            .filter(Boolean)
            .join(', ')
        : null;
    const firstName = capitalizeFirstLetter(leadDetail?.first_name || '');
    const lastName = capitalizeFirstLetter(leadDetail?.last_name || '');
    const treatmentLabel = appointmentSelection?.treatment?.treatment || leadDetail?.lead_treatments?.[0]?.treatment || null;

    // ---------------------------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------------------------
    const handleYes = () => {
        successCalledRef.current = false;
        dispatch(
            RecordAttended(() => {
                successCalledRef.current = true;
                if (variant !== 'card' || reducedMotion) {
                    onDismissed();
                    return;
                }
                setDismissing(true);
                setTimeout(() => onDismissed(), DISMISS_MS);
            }, appointmentSelection)
        );
    };

    const handleNo = () => {
        if (handleNoAttended) handleNoAttended();
    };

    // ---------------------------------------------------------------------------
    // Card variant button block — compact sizing
    // ---------------------------------------------------------------------------
    const cardButtons = (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <LoadingButton
                ref={yesBtnRef}
                data-testid="gate-yes-btn"
                loading={isLoader}
                loadingPosition="start"
                variant="contained"
                disabled={isLoader || appointmentSelection === null}
                onClick={handleYes}
                disableElevation
                aria-label="Yes, mark this patient as attended"
                sx={CRM_PRIMARY_BUTTON_SX}
            >
                Yes, mark attended
            </LoadingButton>
            <Button
                data-testid="gate-no-btn"
                variant="outlined"
                disabled={isLoader}
                onClick={handleNo}
                sx={{ ...CRM_SECONDARY_BUTTON_SX, borderColor: 'rgba(0,0,0,0.23)' }}
            >
                No, choose next step
            </Button>
        </Stack>
    );

    // ---------------------------------------------------------------------------
    // inline-hero variant — larger, bolder Yes button with check icon
    // ---------------------------------------------------------------------------
    if (variant === 'inline-hero') {
        return (
            <Box data-testid="attendance-gate-inline">
                <Typography
                    aria-live="polite"
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#1d1d1f',
                        letterSpacing: '-0.015em',
                        mb: 1.5,
                    }}
                >
                    Did the patient attend?
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <LoadingButton
                        ref={yesBtnRef}
                        data-testid="gate-yes-btn"
                        loading={isLoader}
                        loadingPosition="start"
                        startIcon={<Iconify icon="mdi:check-circle" width={18} height={18} />}
                        variant="contained"
                        disabled={isLoader}
                        onClick={handleYes}
                        disableElevation
                        aria-label="Yes, mark this patient as attended"
                        sx={{
                            ...CRM_PRIMARY_BUTTON_SX,
                            fontSize: '14px',
                            fontWeight: 700,
                            px: 3.5,
                            py: 1.25,
                            minWidth: 160,
                        }}
                    >
                        Yes, mark attended
                    </LoadingButton>
                    <Button
                        data-testid="gate-no-btn"
                        variant="outlined"
                        disabled={isLoader}
                        onClick={handleNo}
                        sx={{ ...CRM_SECONDARY_BUTTON_SX, fontWeight: 500, borderColor: 'rgba(0,0,0,0.23)' }}
                    >
                        No, choose next step
                    </Button>
                </Stack>
            </Box>
        );
    }

    // ---------------------------------------------------------------------------
    // card variant — standalone centred card with appear + dismiss animations
    // ---------------------------------------------------------------------------
    const cardAppearSx = {
        opacity: appeared ? 1 : 0,
        transform: appeared ? 'scale(1)' : 'scale(0.96)',
        transition: reducedMotion
            ? 'none'
            : `opacity ${APPEAR_MS}ms ${EASING}, transform ${APPEAR_MS}ms ${EASING}`,
    };

    const cardDismissSx = dismissing
        ? {
            opacity: 0,
            transform: 'translateY(-20px) scale(0.98)',
            transition: reducedMotion
                ? 'none'
                : `opacity ${DISMISS_MS}ms ${EASING}, transform ${DISMISS_MS}ms ${EASING}`,
            pointerEvents: 'none',
        }
        : {};

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                py: 3,
            }}
        >
            <Box
                data-testid="attendance-gate"
                sx={{
                    width: '100%',
                    maxWidth: 520,
                    bgcolor: '#ffffff',
                    borderRadius: '16px',
                    p: 4,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.08)',
                    ...cardAppearSx,
                    ...cardDismissSx,
                }}
            >
                {/* Subtitle row — appointment meta */}
                {(apptDateLabel || practitionerLabel || treatmentLabel) && (
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        {treatmentLabel && (
                            <Box
                                sx={{
                                    height: 22,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.75,
                                    bgcolor: 'rgba(7, 141, 238, 0.08)',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    color: '#078DEE',
                                }}
                            >
                                <Iconify icon="mdi:tooth-outline" width={12} height={12} />
                                {treatmentLabel}
                            </Box>
                        )}
                        {apptDateLabel && (
                            <Box
                                sx={{
                                    height: 22,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.75,
                                    bgcolor: 'rgba(0, 171, 85, 0.08)',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    color: '#00AB55',
                                }}
                            >
                                <Iconify icon="mdi:calendar-clock" width={12} height={12} />
                                {apptDateLabel}
                            </Box>
                        )}
                        {practitionerLabel && (
                            <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                                · {practitionerLabel}
                            </Typography>
                        )}
                    </Stack>
                )}

                {/* Patient name */}
                <Typography
                    sx={{
                        fontSize: '22px',
                        fontWeight: 700,
                        color: '#1d1d1f',
                        letterSpacing: '-0.022em',
                        lineHeight: 1.2,
                        mb: 1,
                    }}
                >
                    {firstName} {lastName}
                </Typography>

                {/* Gate prompt */}
                <Typography
                    aria-live="polite"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: '#1d1d1f',
                        letterSpacing: '-0.015em',
                        mb: 3,
                    }}
                >
                    Did the patient attend?
                </Typography>

                {cardButtons}
            </Box>
        </Box>
    );
}

AttendanceGate.propTypes = {
    appointmentSelection: PropTypes.object,
    /** Full lead object from Redux. Used for subtitle chips (card variant) and RecordAttended. */
    leadDetail: PropTypes.object,
    /** Called when user clicks "No" — parent closes attended popup + opens FTA. */
    handleNoAttended: PropTypes.func,
    /** Called after dismiss completes on Yes-success. */
    onDismissed: PropTypes.func.isRequired,
    /**
     * "card"        — standalone centred card with animations (for future standalone use).
     * "inline-hero" — question + buttons only, no card chrome; parent handles Collapse.
     */
    variant: PropTypes.oneOf(['card', 'inline-hero']),
};

AttendanceGate.defaultProps = {
    leadDetail: null,
    handleNoAttended: null,
    variant: 'card',
};
